// Shared persona + session + adapter resolution for the chat-surface
// orchestrator pages (TrailheadCore, PersonaChat). Extracted in v0.4
// Phase 1 to keep both orchestrators under the 150-line guardrail.

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PersonaChatAdapter, ToolCallHandler } from '../adapters/personaChatAdapter';
import * as personaService from '../services/personaService';
import * as chatService from '../services/chatService';

export interface PersonaSessionResolution {
  persona: any;
  session: any;
  adapter: PersonaChatAdapter | null;
  newSessionData: any;
  isLoading: boolean;
  isPersonaLoading: boolean;
  isSessionLoading: boolean;
  isSessionCreating: boolean;
  personaError: any;
  refetchSession: () => void;
}

export interface UsePersonaSessionResolutionOpts {
  personaId: string | undefined;
  sessionId: string | undefined;
  /**
   * Fired exactly once when a new chat session is created (no sessionId in URL,
   * persona resolved, createPersonaChat returns). Caller is expected to
   * `navigate(...)` the URL to include the new session id. Receives
   * (effectivePersonaId, newSessionId).
   */
  onSessionCreated: (effectivePersonaId: string, newSessionId: string) => void;
  /**
   * C7 — threaded into the adapter so the caller learns about tool effects
   * (e.g. open_brainstorm) from each turn's tool_calls telemetry.
   */
  onToolCalls?: ToolCallHandler;
}

export function usePersonaSessionResolution({
  personaId,
  sessionId,
  onSessionCreated,
  onToolCalls,
}: UsePersonaSessionResolutionOpts): PersonaSessionResolution {
  const [adapter, setAdapter] = useState<PersonaChatAdapter | null>(null);

  const { data: persona, isLoading: isPersonaLoading, error: personaError } = useQuery(
    ['persona', personaId, sessionId],
    async () => {
      if (sessionId) {
        let sessionData: any = null;
        try {
          sessionData = await chatService.getChatSession(sessionId);
        } catch (error) {
          console.error('Error fetching session data:', error);
          if (personaId && personaId !== 'unknown') {
            return personaService.getPersona(personaId);
          }
          throw error;
        }
        const resolvedPersonaId =
          personaId && personaId !== 'unknown' && personaId !== sessionId
            ? personaId
            : sessionData.persona_id;
        if (resolvedPersonaId && resolvedPersonaId !== 'unknown') {
          try {
            return await personaService.getPersona(resolvedPersonaId);
          } catch (e: any) {
            console.warn(
              `Persona ${resolvedPersonaId} not found (${e?.response?.status ?? e?.message}), falling back to session metadata`,
            );
          }
        }
        return {
          id: resolvedPersonaId || 'unknown',
          name: sessionData.persona_name || 'Unknown Persona',
          shortDescription: 'AI Assistant',
          tags: [],
        };
      }
      return personaId ? personaService.getPersona(personaId) : Promise.reject('No persona ID');
    },
    { enabled: !!personaId || !!sessionId },
  );

  const { data: session, isLoading: isSessionLoading, refetch: refetchSession } = useQuery(
    ['chatSession', sessionId],
    async () => chatService.getChatSession(sessionId),
    { enabled: !!sessionId },
  );

  // Keyed by persona?.id (not the persona ref) so react-query's
  // refetchOnWindowFocus / refetchOnReconnect — which mint a fresh persona
  // reference with identical content — do NOT retrigger adapter recreation.
  // A stable adapter ref keeps ConversationContext's load effect from
  // re-firing during an in-flight send and wiping optimistic message state.
  useEffect(() => {
    if (persona) {
      setAdapter(new PersonaChatAdapter(persona, onToolCalls));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persona?.id]);

  const { data: newSessionData, isLoading: isSessionCreating } = useQuery(
    ['createChatSession', personaId],
    async () => {
      if (!sessionId && persona) {
        // Created via the Trailhead viewer → stamp the belt (spec rev 2 §4.2).
        return chatService.createPersonaChat(persona.id, undefined, { isNew: true, surface: 'trailhead' });
      }
      return null;
    },
    {
      enabled: !!persona && !sessionId,
      onSuccess: (data) => {
        if (data) {
          const effectivePersonaId = personaId || data.persona_id || 'unknown';
          onSessionCreated(effectivePersonaId, data.session_id);
        }
      },
    },
  );

  const isLoading =
    isPersonaLoading || isSessionLoading || (isSessionCreating && !sessionId) || !adapter;

  return {
    persona,
    session,
    adapter,
    newSessionData,
    isLoading,
    isPersonaLoading,
    isSessionLoading,
    isSessionCreating,
    personaError,
    refetchSession,
  };
}
